import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import DefaultData from "../CompanyData/DefaultData";
import PricingSection from "../StaticPages/PricingSection";
import "./GlobalSupplier.css"; // Import the CSS file

// ============= LOCAL SUPPLIER LOGOS =============
// Using local images from public/assets/images/suppliers/
const supplierLogos = {
  tripjack: "/assets/images/suppliers/tripjack.png",
  tbo: "/assets/images/suppliers/tbo.png",
  akbar: "/assets/images/suppliers/akbar.png",
  rezlive: "/assets/images/suppliers/rezlive.png",
  grnconnect: "/assets/images/suppliers/grn.png",
  riya: "/assets/images/suppliers/Riya.png",
  viator: "/assets/images/suppliers/viator.png",
  airiq: "/assets/images/suppliers/airiq.png",
  zotrai: "/assets/images/suppliers/ZotraiLogo.png",
  eTravelSmart: "/assets/images/suppliers/eTravelSmart.png"
};

// Brand colors for each supplier
const brandColors = {
  tripjack: "#2563eb",
  tbo: "#FF4B4B",
  akbar: "#00A859",
  rezlive: "#6B46C1",
  grnconnect: "#2E7D32",
  riya: "#9C27B0",
  viator: "#E31B23",
  airiq: "#0284C7",
  zotrai: "#FF7F00"
};

// Helper function to create URL-friendly slugs
const createSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

// ============= HERO FEATURE COMPONENT =============
const HeroFeature = ({ icon, title, text }) => (
  <div className="globalsupplier-hero-feature">
    <div className="globalsupplier-hero-feature-icon">
      <i className={icon}></i>
    </div>
    <div className="globalsupplier-hero-feature-content">
      <h6 className="globalsupplier-hero-feature-title">{title}</h6>
      <p className="globalsupplier-hero-feature-text">{text}</p>
    </div>
  </div>
);

// ============= VIDEO DEMO MODAL COMPONENT (UPDATED FOR LOCAL MP4) =============
const VideoDemoModal = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    // Pause video when modal closes
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="globalsupplier-video-modal-overlay" onClick={onClose}>
      <div className="globalsupplier-video-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="globalsupplier-video-close-btn" onClick={onClose}>
          <i className="ri-close-line"></i>
        </button>
        <div className="globalsupplier-video-wrapper">
          <video
            ref={videoRef}
            controls
            autoPlay
            className="globalsupplier-local-video"
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  );
};

// ============= NETWORK VISUALIZATION WITH BIG ZOTRAI =============
const NetworkVisualization = () => (
  <div className="globalsupplier-network-viz">
    {/* Central Hub - Zotrai (Big & Highlighted) */}
    <div className="globalsupplier-central-hub">
      <div className="globalsupplier-pulse-ring globalsupplier-ring-1"></div>
      <div className="globalsupplier-pulse-ring globalsupplier-ring-2"></div>
      <div className="globalsupplier-pulse-ring globalsupplier-ring-3"></div>
      <div className="globalsupplier-pulse-ring globalsupplier-ring-4"></div>
      
      <div className="globalsupplier-hub-logo-wrapper">
        <div className="globalsupplier-hub-logo" style={{ background: `linear-gradient(145deg, ${brandColors.zotrai}, ${brandColors.zotrai}dd)` }}>
          <img 
            src={supplierLogos.zotrai}
            alt="Zotrai" 
            className="globalsupplier-hub-logo-img"
          />
        </div>
      </div>
    </div>

    {/* Supplier Nodes - All connected to Zotrai */}
    <div className="globalsupplier-supplier-node globalsupplier-node-1">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.tripjack}30` }}>
        <img src={supplierLogos.tripjack} alt="Trip Jack" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-2">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.tbo}30` }}>
        <img src={supplierLogos.tbo} alt="TBO" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-3">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.akbar}30` }}>
        <img src={supplierLogos.akbar} alt="Akbar Travels" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-4">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.rezlive}30` }}>
        <img src={supplierLogos.rezlive} alt="RezLive" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-5">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.grnconnect}30` }}>
        <img src={supplierLogos.grnconnect} alt="GRN Connect" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-6">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.riya}30` }}>
        <img src={supplierLogos.riya} alt="Riya Travel" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-7">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.viator}30` }}>
        <img src={supplierLogos.viator} alt="Viator" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    <div className="globalsupplier-supplier-node globalsupplier-node-8">
      <div className="globalsupplier-node-logo" style={{ borderColor: `${brandColors.airiq}30` }}>
        <img src={supplierLogos.airiq} alt="Air iQ" className="globalsupplier-node-logo-img" />
      </div>
    </div>

    {/* SVG Connection Lines */}
    <svg className="globalsupplier-connection-svg" viewBox="0 0 600 600">
      <line x1="300" y1="300" x2="150" y2="150" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="450" y2="150" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="100" y2="250" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="500" y2="250" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="150" y2="450" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="450" y2="450" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="300" y2="80" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="300" y1="300" x2="300" y2="520" stroke="rgba(255,127,0,0.3)" strokeWidth="2" strokeDasharray="8,8">
        <animate attributeName="stroke-dashoffset" from="0" to="16" dur="3s" repeatCount="indefinite" />
      </line>
    </svg>
  </div>
);

// ============= CONNECTED SUPPLIERS LOGOS SECTION (NEW ATTRACTIVE SECTION) =============
const ConnectedSuppliersLogos = () => (
  <section className="globalsupplier-connected-suppliers-section">
    <div className="globalsupplier-container">
      <div className="globalsupplier-connected-suppliers-header">
        <span className="globalsupplier-connected-badge">
          <i className="ri-handshake-line"></i> OUR INTEGRATED SUPPLIERS
        </span>
        <h2 className="globalsupplier-connected-title">
          Connect With <span className="globalsupplier-gradient-text">Leading Travel Suppliers</span>
        </h2>
        <p className="globalsupplier-connected-subtitle">
          Direct integrations with industry-leading travel suppliers worldwide
        </p>
      </div>

      <div className="globalsupplier-logos-marquee">
        <div className="globalsupplier-logos-track">
          {/* First set of logos */}
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.tripjack}30` }}>
              <img src={supplierLogos.tripjack} alt="Tripjack" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Tripjack</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.tbo}30` }}>
              <img src={supplierLogos.tbo} alt="TBO" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">TBO</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.akbar}30` }}>
              <img src={supplierLogos.akbar} alt="Akbar Travels" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Akbar Travels</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.rezlive}30` }}>
              <img src={supplierLogos.rezlive} alt="RezLive" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">RezLive</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.grnconnect}30` }}>
              <img src={supplierLogos.grnconnect} alt="GRN Connect" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">GRN Connect</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.riya}30` }}>
              <img src={supplierLogos.riya} alt="Riya Travel" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Riya Travel</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.viator}30` }}>
              <img src={supplierLogos.viator} alt="Viator" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Viator</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.airiq}30` }}>
              <img src={supplierLogos.airiq} alt="Air iQ" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Air iQ</span>
          </div>
          
          {/* Duplicate set for seamless infinite scroll */}
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.tripjack}30` }}>
              <img src={supplierLogos.tripjack} alt="Tripjack" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Tripjack</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.tbo}30` }}>
              <img src={supplierLogos.tbo} alt="TBO" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">TBO</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.akbar}30` }}>
              <img src={supplierLogos.akbar} alt="Akbar Travels" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Akbar Travels</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.rezlive}30` }}>
              <img src={supplierLogos.rezlive} alt="RezLive" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">RezLive</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.grnconnect}30` }}>
              <img src={supplierLogos.grnconnect} alt="GRN Connect" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">GRN Connect</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.riya}30` }}>
              <img src={supplierLogos.riya} alt="Riya Travel" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Riya Travel</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.viator}30` }}>
              <img src={supplierLogos.viator} alt="Viator" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Viator</span>
          </div>
          <div className="globalsupplier-logo-item">
            <div className="globalsupplier-logo-wrapper" style={{ borderColor: `${brandColors.airiq}30` }}>
              <img src={supplierLogos.airiq} alt="Air iQ" className="globalsupplier-logo-image" />
            </div>
            <span className="globalsupplier-logo-name">Air iQ</span>
          </div>
        </div>
      </div>

      <div className="globalsupplier-supplier-stats-mini">
        <div className="globalsupplier-stat-mini-item">
          <span className="globalsupplier-stat-mini-number">1M+</span>
          <span className="globalsupplier-stat-mini-label">Hotel Properties</span>
        </div>
        <div className="globalsupplier-stat-mini-item">
          <span className="globalsupplier-stat-mini-number">80+</span>
          <span className="globalsupplier-stat-mini-label">Airlines</span>
        </div>
        <div className="globalsupplier-stat-mini-item">
          <span className="globalsupplier-stat-mini-number">300K+</span>
          <span className="globalsupplier-stat-mini-label">Activities</span>
        </div>
      </div>
    </div>
  </section>
);

// ============= WHY ZOTRAI SECTION (ENLARGED) =============
const WhyZotraiSection = () => (
  <section className="globalsupplier-why-zotrai-section">
    <div className="globalsupplier-container">
      <div className="row align-items-center">
        <div className="col-lg-6">
          <div className="globalsupplier-why-zotrai-content">
            <span className="globalsupplier-why-badge">
              <i className="ri-question-line"></i> WHY ZOTRAI?
            </span>
            <h2 className="globalsupplier-why-title display-4 fw-bold mb-4">
              The Smart Choice for <span className="globalsupplier-highlight">Travel Businesses</span>
            </h2>
            <p className="globalsupplier-why-description lead mb-5">
              Zotrai isn't just another platform - it's your complete travel business solution. 
              We combine powerful supplier integrations with ready-to-use website templates 
              to help you launch and scale faster.
            </p>
            
            <div className="globalsupplier-why-features-grid">
              <div className="globalsupplier-why-feature-item">
                <div className="globalsupplier-why-feature-icon" style={{ background: `${brandColors.zotrai}15` }}>
                  <i className="ri-rocket-line" style={{ color: brandColors.zotrai }}></i>
                </div>
                <div>
                  <h4 className="h5 fw-bold">Launch in Days, Not Months</h4>
                  <p className="text-muted">Pre-built templates and integrations get you live faster</p>
                </div>
              </div>
              
              <div className="globalsupplier-why-feature-item">
                <div className="globalsupplier-why-feature-icon" style={{ background: `${brandColors.zotrai}15` }}>
                  <i className="ri-hand-coin-line" style={{ color: brandColors.zotrai }}></i>
                </div>
                <div>
                  <h4 className="h5 fw-bold">Cost-Effective Solution</h4>
                  <p className="text-muted">Save thousands on development and multiple integrations</p>
                </div>
              </div>
              
              <div className="globalsupplier-why-feature-item">
                <div className="globalsupplier-why-feature-icon" style={{ background: `${brandColors.zotrai}15` }}>
                  <i className="ri-shield-star-line" style={{ color: brandColors.zotrai }}></i>
                </div>
                <div>
                  <h4 className="h5 fw-bold">Enterprise-Grade Reliability</h4>
                  <p className="text-muted">99.9% uptime with 24/7 technical support</p>
                </div>
              </div>
              
              <div className="globalsupplier-why-feature-item">
                <div className="globalsupplier-why-feature-icon" style={{ background: `${brandColors.zotrai}15` }}>
                  <i className="ri-group-line" style={{ color: brandColors.zotrai }}></i>
                </div>
                <div>
                  <h4 className="h5 fw-bold">Agent Network</h4>
                  <p className="text-muted">Instant access to verified B2B buyers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-lg-6">
          <div className="globalsupplier-why-zotrai-showcase">
            <div className="globalsupplier-showcase-card">
              <div className="globalsupplier-showcase-header">
                <i className="ri-bar-chart-2-line"></i>
                <span>Your Success with Zotrai</span>
              </div>
             
              <div className="globalsupplier-showcase-quote">
                <i className="ri-double-quotes-L"></i>
              <p>"Zotrai helped us launch our fully integrated travel portal in minutes — with all major suppliers connected instantly."</p>
                <div className="globalsupplier-quote-author">
                  <strong>- Travel Agency Partner</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============= CHECK ITEM COMPONENT =============
const CheckItem = ({ title, text }) => (
  <div className="globalsupplier-check-item">
    <i className="ri-checkbox-circle-fill globalsupplier-check-icon"></i>
    <div>
      <p className="globalsupplier-check-text">
        <strong>{title}:</strong> {text}
      </p>
    </div>
  </div>
);

// ============= STEP CARD COMPONENT =============
const StepCard = ({ number, title, text }) => (
  <div className="globalsupplier-step-card">
    <div className="globalsupplier-step-number" style={{ backgroundColor: DefaultData.THEMECOLOR1 }}>
      {number}
    </div>
    <h5 className="globalsupplier-step-title">{title}</h5>
    <p className="globalsupplier-step-text">{text}</p>
  </div>
);

// ============= INVENTORY CARD COMPONENT =============
const InventoryCard = ({ icon, title }) => (
  <div className="globalsupplier-inventory-card">
    <i className={`${icon} globalsupplier-inventory-icon`} style={{ color: DefaultData.THEMECOLOR1 }}></i>
    <h6 className="globalsupplier-inventory-title">{title}</h6>
  </div>
);

// ============= AI FEATURE CARD COMPONENT =============
const AIFeatureCard = ({ icon, title, text, badge }) => (
  <div className="globalsupplier-ai-card">
    <div className="globalsupplier-ai-card-header">
      <div className="globalsupplier-ai-icon">
        <i className={`${icon} globalsupplier-ai-icon-img`}></i>
      </div>
      {badge && <span className="globalsupplier-ai-badge">{badge}</span>}
    </div>
    <h5 className="globalsupplier-ai-title">{title}</h5>
    <p className="globalsupplier-ai-text">{text}</p>
  </div>
);

// ============= SUPPLIER CARD WITH REAL LOGO (UPDATED WITH SEO-FRIENDLY SLUG) =============
const SupplierCard = ({ supplier, companyName = "Zotrai" }) => (
  <div className="globalsupplier-supplier-card-modern">
    <div className="globalsupplier-supplier-card-header" style={{ borderBottomColor: supplier.brandColor }}>
      <div className="globalsupplier-supplier-card-header-content">
        <div className="globalsupplier-supplier-logo-circle">
          <img 
            src={supplier.logo} 
            alt={supplier.name}
            className="globalsupplier-supplier-logo-img"
          />
        </div>
        <div>
          <h5 className="globalsupplier-supplier-name-modern">{supplier.name}</h5>
          <span className="globalsupplier-connected-badge">
            <i className="ri-check-line"></i> Integrated with {companyName}
          </span>
        </div>
      </div>
    </div>
    
    <div className="globalsupplier-supplier-card-body">
      <p className="globalsupplier-supplier-description-modern">{supplier.description}</p>
      
      <div className="globalsupplier-supplier-section">
        <h6 className="globalsupplier-section-title-modern">
          <i className="ri-star-fill" style={{ color: supplier.brandColor }}></i>
          Key Features
        </h6>
        <div className="globalsupplier-features-grid">
          {supplier.features.map((feature, index) => (
            <span key={index} className="globalsupplier-feature-tag">{feature}</span>
          ))}
        </div>
      </div>

      <div className="globalsupplier-supplier-section">
        <h6 className="globalsupplier-section-title-modern">
          <i className="ri-database-2-line" style={{ color: supplier.brandColor }}></i>
          Inventory Types
        </h6>
        <div className="globalsupplier-inventory-grid-modern">
          {supplier.inventory.map((item, index) => (
            <span key={index} className="globalsupplier-inventory-tag">
              <i className={item.icon}></i>
              {item.name}
            </span>
          ))}
        </div>
      </div>

      <div className="globalsupplier-supplier-footer">
        <div>
          <span className="globalsupplier-status-text">Integration Status</span>
          <strong className="globalsupplier-live-status">
            <i className="ri-flashlight-fill"></i> Active • Live
          </strong>
        </div>
        <Link
          to={`/supplier/${supplier.slug}`} // Using slug instead of id for SEO-friendly URLs
          className="globalsupplier-view-details-btn"
          style={{ borderColor: supplier.brandColor, color: supplier.brandColor }}
        >
          View Details
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  </div>
);

// ============= SUPPLIER STATS COMPONENT =============
const SupplierStats = ({ supplier }) => (
  <div className="globalsupplier-supplier-stats-card">
    <div className="globalsupplier-stats-header">
      <div className="globalsupplier-stats-icon">
        <img src={supplier.logo} alt={supplier.name} className="globalsupplier-stats-logo" />
      </div>
      <div>
        <h6 className="globalsupplier-stats-name">{supplier.name}</h6>
        <div className="globalsupplier-stats-status">
          <span className="globalsupplier-live-dot"></span>
          <span className="globalsupplier-stats-live">Live via API</span>
        </div>
      </div>
    </div>
    
    <div className="globalsupplier-stats-grid">
      <div className="globalsupplier-stat-item">
        <span className="globalsupplier-stat-label">Total Products</span>
        <span className="globalsupplier-stat-value">{supplier.stats?.products || '2.5K+'}</span>
      </div>
      <div className="globalsupplier-stat-item">
        <span className="globalsupplier-stat-label">Active Agents</span>
        <span className="globalsupplier-stat-value">{supplier.stats?.agents || '1.2K+'}</span>
      </div>
      <div className="globalsupplier-stat-item">
        <span className="globalsupplier-stat-label">Monthly Bookings</span>
        <span className="globalsupplier-stat-value">{supplier.stats?.bookings || '15K+'}</span>
      </div>
      <div className="globalsupplier-stat-item">
        <span className="globalsupplier-stat-label">Coverage</span>
        <span className="globalsupplier-stat-value">{supplier.stats?.coverage || 'Global'}</span>
      </div>
    </div>
  </div>
);

// ============= ZOTRAI PLATFORM FEATURES =============
const ZotraiPlatformFeatures = () => (
  <section className="globalsupplier-platform-section">
    <div className="globalsupplier-container">
     {/* ===== WHY CHOOSE ZOTRAI? SECTION (Enhanced) ===== */}
<section className="globalsupplier-why-choose-section">
  <div className="globalsupplier-container">
    <div className="globalsupplier-why-choose-header">
      <span className="globalsupplier-why-choose-badge">
        <i className="ri-star-smile-line"></i> WHY TRAVEL BUSINESSES LOVE US
      </span>
      <h2 className="globalsupplier-why-choose-title">
        Everything You Need to <span className="globalsupplier-gradient-text">Scale Your Travel Business</span>
      </h2>
      <p className="globalsupplier-why-choose-subtitle">
        One powerful platform that combines global supplier access with ready-to-launch solutions
      </p>
    </div>

    <div className="globalsupplier-why-choose-grid">
      {/* Card 1 - Single Click Access */}
      <div className="globalsupplier-why-card">
        <div className="globalsupplier-card-inner">
          <div className="globalsupplier-card-front">
            <div className="globalsupplier-card-icon-wrapper" style={{ background: "linear-gradient(135deg, #FF7F0015, #FF7F0005)" }}>
              <div className="globalsupplier-card-icon">
                <i className="ri-mouse-line" style={{ color: "#FF7F00" }}></i>
              </div>
            </div>
            <h3 className="globalsupplier-card-title">Single Click Access</h3>
            <p className="globalsupplier-card-description">Connect to all suppliers with one integration</p>
            <div className="globalsupplier-card-hover-indicator">
              <span>Hover to see benefits</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </div>
          <div className="globalsupplier-card-back">
            <h4 className="globalsupplier-back-title">What you get:</h4>
            <ul className="globalsupplier-back-features">
              <li><i className="ri-checkbox-circle-fill"></i> Unified dashboard</li>
              <li><i className="ri-checkbox-circle-fill"></i> Single API key</li>
              <li><i className="ri-checkbox-circle-fill"></i> Centralized billing</li>
              <li><i className="ri-checkbox-circle-fill"></i> No multiple logins</li>
              <li><i className="ri-checkbox-circle-fill"></i> No separate contracts</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Card 2 - Ready-to-Use Templates */}
      <div className="globalsupplier-why-card globalsupplier-featured">
        <div className="globalsupplier-card-badge">MOST POPULAR</div>
        <div className="globalsupplier-card-inner">
          <div className="globalsupplier-card-front">
            <div className="globalsupplier-card-icon-wrapper" style={{ background: "linear-gradient(135deg, #FF7F0020, #FF7F0010)" }}>
              <div className="globalsupplier-card-icon">
                <i className="ri-layout-grid-line" style={{ color: "#FF7F00" }}></i>
              </div>
            </div>
            <h3 className="globalsupplier-card-title">100+ Templates</h3>
            <p className="globalsupplier-card-description">Professionally designed travel websites</p>
            <div className="globalsupplier-card-hover-indicator">
              <span>Explore templates</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </div>
          <div className="globalsupplier-card-back">
            <h4 className="globalsupplier-back-title">Template categories:</h4>
            <ul className="globalsupplier-back-features">
              <li><i className="ri-checkbox-circle-fill"></i> Flight booking templates</li>
              <li><i className="ri-checkbox-circle-fill"></i> Hotel search layouts</li>
              <li><i className="ri-checkbox-circle-fill"></i> Package tour designs</li>
              <li><i className="ri-checkbox-circle-fill"></i> Holiday packages</li>
              <li><i className="ri-checkbox-circle-fill"></i> Corporate travel portals</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Card 3 - Launch Your Website */}
      <div className="globalsupplier-why-card">
        <div className="globalsupplier-card-inner">
          <div className="globalsupplier-card-front">
            <div className="globalsupplier-card-icon-wrapper" style={{ background: "linear-gradient(135deg, #FF7F0015, #FF7F0005)" }}>
              <div className="globalsupplier-card-icon">
                <i className="ri-rocket-line" style={{ color: "#FF7F00" }}></i>
              </div>
            </div>
            <h3 className="globalsupplier-card-title">Instant Launch</h3>
            <p className="globalsupplier-card-description">Go live in minutes with your branded portal</p>
            <div className="globalsupplier-card-hover-indicator">
              <span>See whats included</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </div>
          <div className="globalsupplier-card-back">
            <h4 className="globalsupplier-back-title">Everything included:</h4>
            <ul className="globalsupplier-back-features">
              <li><i className="ri-checkbox-circle-fill"></i> Custom domain</li>
              <li><i className="ri-checkbox-circle-fill"></i> SSL certificate</li>
              <li><i className="ri-checkbox-circle-fill"></i> Mobile responsive</li>
              <li><i className="ri-checkbox-circle-fill"></i> SEO optimized</li>
              <li><i className="ri-checkbox-circle-fill"></i> 24/7 support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    {/* Stats Section */}
    <div className="globalsupplier-stats-row">
      <div className="globalsupplier-stat-item-enhanced">
        <span className="globalsupplier-stat-number">50M+</span>
        <span className="globalsupplier-stat-label">Annual Bookings</span>
      </div>
      <div className="globalsupplier-stat-item-enhanced">
        <span className="globalsupplier-stat-number">99.9%</span>
        <span className="globalsupplier-stat-label">Uptime Guarantee</span>
      </div>
      <div className="globalsupplier-stat-item-enhanced">
        <span className="globalsupplier-stat-number">24/7</span>
        <span className="globalsupplier-stat-label">Customer Support</span>
      </div>
    </div>
  </div>
</section>

      {/* Pricing Card */}
      <div className="globalsupplier-pricing-card">
        <div className="globalsupplier-pricing-content">
          <div className="globalsupplier-pricing-left">
            <div className="globalsupplier-pricing-badge">LAUNCH OFFER</div>
            <h3 className="globalsupplier-pricing-card-title">Start Your Travel Business Today</h3>
            <p className="globalsupplier-pricing-description">
              Complete website solution with all supplier integrations included
            </p>
            <div className="globalsupplier-pricing-features">
              <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> Custom domain</span>
              <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> SSL security</span>
              <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> 100+ templates</span>
              <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> All suppliers</span>
              <span><i className="ri-checkbox-circle-fill" style={{ color: "#10B981" }}></i> 24/7 support</span>
            </div>
          </div>
          <div className="globalsupplier-pricing-right">
            <div className="globalsupplier-pricing-amount">
             
              <span className="globalsupplier-amount">2999</span>
              <span className="globalsupplier-period">INR/month</span>
            </div>
            <Link to="/signup" className="globalsupplier-pricing-cta">
              Get Started <i className="ri-arrow-right-line"></i>
            </Link>
            <p className="globalsupplier-pricing-note">No setup fee • Cancel anytime</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============= MAIN COMPONENT =============
export default function SupplierPartners() {
  const [countries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Company Information
  const companyInfo = {
    name: "Zotrai",
    themeColor: DefaultData.THEMECOLOR1,
    description: "Your trusted B2B travel technology partner"
  };

  // Local MP4 video path
  const demoVideoUrl = "/videos/Website-demo.mp4"; // Change this to your actual video filename

  // Thumbnail image for video preview
  const videoThumbnail = "/assets/images/video-thumbnail.png"; // Optional: Add a thumbnail image

  useEffect(() => {
    // Fetch supplier data
    const fetchSuppliers = async () => {
      try {
        setLoading(true);
        // Mock data with local logos and SEO-friendly slugs
        const mockSuppliers = [
          {
            id: 1,
            name: "Trip jack",
            slug: createSlug("Trip jack"), // This will create "trip-jack"
            logo: supplierLogos.tripjack,
            brandColor: brandColors.tripjack,
            description: "Leading provider of flight inventory and airline content with real-time availability and competitive pricing.",
            features: ["Real-time Flight Search", "Group Bookings", "Multi-Airline", "24/7 Support"],
            inventory: [
              { name: "Flights", icon: "ri-plane-line" },
              { name: "Hotels", icon: "ri-hotel-line" }
            ],
            stats: { products: "50K+", agents: "2.5K+", bookings: "45K+", coverage: "180+" }
          },
          {
            id: 2,
            name: "TBO",
            slug: createSlug("TBO"), // This will create "tbo"
            logo: supplierLogos.tbo,
            brandColor: brandColors.tbo,
            description: "Global travel distribution platform offering comprehensive hotel inventory and dynamic packaging capabilities.",
            features: ["Global Hotels", "Dynamic Packaging", "B2B Rates", "Instant Confirmation"],
            inventory: [
              { name: "Flights", icon: "ri-plane-line" },
              { name: "Hotels", icon: "ri-hotel-line" },
              { name: "Packages", icon: "ri-suitcase-2-line" },
              { name: "Activities", icon: "ri-map-pin-line" }
            ],
            stats: { products: "1M+", agents: "5K+", bookings: "100K+", coverage: "220+" }
          },
          {
            id: 3,
            name: "Akbar travels",
            slug: createSlug("Akbar travels"), // This will create "akbar-travels"
            logo: supplierLogos.akbar,
            brandColor: brandColors.akbar,
            description: "Integrated travel solutions provider specializing in hotel accommodations and holiday packages across India and Middle East.",
            features: ["Indian Hotels", "Middle East", "Holidays", "Corporate Travel"],
            inventory: [
              { name: "Flights", icon: "ri-plane-line" },
              { name: "Hotels", icon: "ri-hotel-line" },
              { name: "Holidays", icon: "ri-sun-line" },
              { name: "Umrah", icon: "ri-mosque-line" }
            ],
            stats: { products: "25K+", agents: "1.8K+", bookings: "30K+", coverage: "50+" }
          },
          {
            id: 4,
            name: "Rezlive",
            slug: createSlug("Rezlive"), // This will create "rezlive"
            logo: supplierLogos.rezlive,
            brandColor: brandColors.rezlive,
            description: "Cloud-based hotel distribution platform connecting suppliers with travel agents through seamless API integration.",
            features: ["Hotel API", "Channel Manager", "Rate Management", "Real-time Availability"],
            inventory: [
              { name: "Hotels", icon: "ri-hotel-line" },
              { name: "Resorts", icon: "ri-umbrella-line" },
              { name: "Serviced Apts", icon: "ri-home-8-line" }
            ],
            stats: { products: "100K+", agents: "3K+", bookings: "75K+", coverage: "150+" }
          },
          {
            id: 5,
            name: "Grn connect",
            slug: createSlug("Grn connect"), // This will create "grn-connect"
            logo: supplierLogos.grnconnect,
            brandColor: brandColors.grnconnect,
            description: "Eco-conscious travel supplier specializing in sustainable accommodations and green travel initiatives.",
            features: ["Eco-hotels", "Sustainable Tours", "Carbon Offset", "Green Certification"],
            inventory: [
              { name: "Hotels", icon: "ri-hotel-line" },
              { name: "Resorts", icon: "ri-umbrella-line" },
              { name: "Serviced Apts", icon: "ri-home-8-line" }
            ],
            stats: { products: "5K+", agents: "800+", bookings: "8K+", coverage: "75+" }
          },
          {
            id: 6,
            name: "Riya",
            slug: createSlug("Riya travel"), // This will create "riya"
            logo: supplierLogos.riya,
            brandColor: brandColors.riya,
            description: "Premium travel supplier offering curated holiday experiences and luxury travel packages worldwide.",
            features: ["Luxury Travel", "Custom Itineraries", "VIP Services", "Concierge"],
            inventory: [
              { name: "Flights", icon: "ri-plane-line" },
              { name: "Hotels", icon: "ri-hotel-line" },
            ],
            stats: { products: "10K+", agents: "1.2K+", bookings: "12K+", coverage: "100+" }
          },
          {
            id: 7,
            name: "Viator",
            slug: createSlug("Viator"), // This will create "viator"
            logo: supplierLogos.viator,
            brandColor: brandColors.viator,
            description: "World's largest marketplace for travel experiences, tours, and activities with instant booking confirmation.",
            features: ["Tours & Activities", "Skip-the-line", "Local Guides", "Mobile Tickets"],
            inventory: [
              { name: "Tours", icon: "ri-guide-line" },
              { name: "Activities", icon: "ri-run-line" },
              { name: "Attractions", icon: "ri-landmark-line" }
            ],
            stats: { products: "300K+", agents: "4K+", bookings: "200K+", coverage: "190+" }
          },
          {
            id: 8,
            name: "Air iQ",
            slug: createSlug("Air iQ"), // This will create "air-iq"
            logo: supplierLogos.airiq,
            brandColor: brandColors.airiq,
            description: "AI-powered flight intelligence platform offering predictive pricing and smart inventory management.",
            features: ["AI Pricing", "Smart Allotments", "Forecast", "Analytics"],
            inventory: [
              { name: "Flights", icon: "ri-plane-line" },
              { name: "Group Fares", icon: "ri-team-line" },
            ],
            stats: { products: "15K+", agents: "1.5K+", bookings: "25K+", coverage: "120+" }
          },
          {
            id: 9,
            name: "eTravelSmart",
            slug: createSlug("eTravelSmart"), // This will create "air-iq"
            logo: supplierLogos.eTravelSmart,
            brandColor: brandColors.eTravelSmart,
            description: "eTravelSmart is a smart and intuitive online bus ticket booking portal, offering online bus ticket reservation services across India.",
            features: ["Bus Invetory", "Private","Government","A/C, Non A/C, and Volvo)"],
            inventory: [
              { name: "Bus", icon: "ri-bus-line" },
              { name: "Group Fares", icon: "ri-team-line" },
            ],
            stats: { routes: "80000+", agents: "1.5K+", bookings: "25K+", coverage: "120+" }
          }
        ];

        setSuppliers(mockSuppliers);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  return (
    <div className="globalsupplier-main-wrapper">
      {/* ===== HERO SECTION ===== */}
      <section className="globalsupplier-hero-section" style={{ background: `linear-gradient(135deg, ${DefaultData.THEMECOLOR1} 0%, #0f172a 100%)` }}>
        <div className="globalsupplier-hero-bg-orb"></div>
        <div className="globalsupplier-hero-bg-orb-2"></div>
        
        <div className="globalsupplier-container">
          <div className="globalsupplier-hero-grid">
            {/* Left Content */}
            <div>
              <div className="globalsupplier-hero-badge">
                <i className="ri-handshake-line"></i>
                PRE INTEGRATED SUPPLIERS
              </div>
              
              <h1 className="globalsupplier-hero-title">
                Connect with Supplier API's via{" "}
                <span className="globalsupplier-gradient-text">Single Zotrai API</span>
              </h1>
              
              <div className="globalsupplier-price-highlight">
                <div className="globalsupplier-price-tag">
                  <span className="globalsupplier-price-label">Price Starting from</span>
                  <span className="globalsupplier-price-value">2,999</span>
                  <span className="globalsupplier-price-period">INR/month</span>
                </div>
                <div className="globalsupplier-price-badge">💰 Save 70% on integration costs</div>
              </div>

              <div className="globalsupplier-hero-ctas">
                <Link to="/" className="globalsupplier-hero-cta-primary">
                  Start Free Trial <i className="ri-arrow-right-line"></i>
                </Link>
                <button 
                  className="globalsupplier-hero-cta-secondary"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  <i className="ri-play-circle-line"></i>
                  Watch Demo
                </button>
              </div>

              {/* Trust Badges */}
              <div className="globalsupplier-trust-badges">
                <div className="globalsupplier-trust-item">
                  <i className="ri-shield-check-line"></i>
                  <span>No Credit Card Required</span>
                </div>
                <div className="globalsupplier-trust-item">
                  <i className="ri-time-line"></i>
                  <span>5-min Setup</span>
                </div>
                <div className="globalsupplier-trust-item">
                  <i className="ri-customer-service-line"></i>
                  <span>24/7 Support</span>
                </div>
              </div>

              {/* Features */}
              <div className="globalsupplier-hero-features">
                <div>
                  <HeroFeature
                    icon="ri-flight-takeoff-line"
                    title="80+ Airlines"
                    text="Emirates, Qatar, Etihad, Singapore Airlines & more"
                  />
                  <HeroFeature
                    icon="ri-hotel-line"
                    title="3.5M+ Properties"
                    text="Hotels, villas, apartments worldwide"
                  />
                  <HeroFeature
                    icon="ri-camera-line"
                    title="300K+ Activities"
                    text="Tours, attractions, experiences globally"
                  />
                </div>
                <div>
                  <HeroFeature
                    icon="ri-group-line"
                    title="Growing Agent Network"
                    text="Verified B2B travel buyers"
                  />
                  <HeroFeature
                    icon="ri-flashlight-line"
                    title="Real-time API"
                    text="Live inventory, instant confirmation"
                  />
                  <HeroFeature
                    icon="ri-shield-check-line"
                    title="99.9% Uptime"
                    text="Enterprise-grade reliability"
                  />
                </div>
              </div>
            </div>

            {/* Right - Network Visualization */}
            <div>
              <NetworkVisualization />
            </div>
          </div>
        </div>

        <div className="globalsupplier-hero-divider"></div>
      </section>
      
      {/* ===== CONNECTED SUPPLIERS LOGOS SECTION (NEW) ===== */}
      <ConnectedSuppliersLogos />
      
      {/* ===== ZOTRAI PLATFORM FEATURES ===== */}
      <ZotraiPlatformFeatures />
      
      {/* ===== VIDEO DEMO SECTION ===== */}
      <section className="globalsupplier-video-demo-section">
        <div className="globalsupplier-container">
          <div className="globalsupplier-video-demo-header">
            <span className="globalsupplier-video-badge">
              <i className="ri-play-circle-line"></i> SEE IT IN ACTION
            </span>
            <h2 className="globalsupplier-video-title">
              Watch How Zotrai Transforms Your <span className="globalsupplier-highlight">Travel Business</span>
            </h2>
            <p className="globalsupplier-video-subtitle">
              See how easy it is to connect with multiple suppliers and launch your travel portal in minutes
            </p>
          </div>

          <div className="globalsupplier-video-demo-grid">
            {/* Video Preview Card */}
            <div className="globalsupplier-video-preview-card" onClick={() => setIsVideoModalOpen(true)}>
              <div className="globalsupplier-video-thumbnail">
                {videoThumbnail ? (
                  <img 
                    src={videoThumbnail} 
                    alt="Video Demo Thumbnail"
                    className="globalsupplier-thumbnail-img"
                  />
                ) : (
                  <div className="globalsupplier-thumbnail-placeholder">
                    <i className="ri-film-line"></i>
                    <span>Click to play demo</span>
                  </div>
                )}
                <div className="globalsupplier-play-button">
                  <i className="ri-play-circle-fill"></i>
                </div>
                <div className="globalsupplier-video-duration">Demo Video</div>
              </div>
              <div className="globalsupplier-video-info">
                <h4>Complete Platform Walkthrough</h4>
                <p>See how to connect suppliers, manage bookings, and launch your website</p>
              </div>
            </div>

          {/* Steps Section */}
<div className="globalsupplier-video-steps">
  <h3 className="globalsupplier-steps-title">Quick 3-Step Setup</h3>
  
  <div className="globalsupplier-steps-timeline">
    <div className="globalsupplier-step-item">
      <div className="globalsupplier-step-number" style={{ background: "#FF7F00" }}>1</div>
      <div className="globalsupplier-step-content">
        <h4>Sign Up & Choose Plan</h4>
        <p>Create your account in 30 seconds - no credit card required</p>
      </div>
    </div>

    <div className="globalsupplier-step-item">
      <div className="globalsupplier-step-number" style={{ background: "#FF7F00" }}>2</div>
      <div className="globalsupplier-step-content">
        <h4>Select Your Suppliers</h4>
        <p>Choose from leading travel suppliers with one click</p>
      </div>
    </div>

    <div className="globalsupplier-step-item">
      <div className="globalsupplier-step-number" style={{ background: "#FF7F00" }}>3</div>
      <div className="globalsupplier-step-content">
        <h4>Launch Your Portal</h4>
        <p>Go live with your branded website in minutes</p>
      </div>
    </div>
  </div>

  <div className="globalsupplier-steps-cta">
    <Link to="/" className="globalsupplier-steps-cta-btn">
      Start Your Free Trial <i className="ri-arrow-right-line"></i>
    </Link>
    <p className="globalsupplier-steps-note">✓ No credit card required • ✓ Cancel anytime</p>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* ===== WHY ZOTRAI SECTION (ENLARGED) ===== */}
      <WhyZotraiSection />

    

      {/* ===== ALL SUPPLIERS GRID ===== */}
      <section className="globalsupplier-py-5 globalsupplier-bg-light">
        <div className="globalsupplier-container">
          <div className="row mb-5">
            <div className="col-lg-8">
              <h2 className="display-6 fw-bold mb-3">Connected Suppliers</h2>
              <p className="text-muted fs-5">
                Direct integrations with industry-leading suppliers
              </p>
            </div>
            <div className="col-lg-4 text-lg-end text-start">
              <div className="bg-white p-3 rounded-4 shadow-sm d-inline-block">
                <span className="text-muted me-2">
                  <i className="ri-checkbox-circle-fill text-success me-1"></i>
                  Live Connections:
                </span>
                <span className="fw-bold">Growing Network</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: DefaultData.THEMECOLOR1 }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading supplier information...</p>
            </div>
          ) : (
            <div className="row g-4">
              {suppliers.map((supplier) => (
                <div className="col-lg-4 col-md-6" key={supplier.id}>
                  <SupplierCard 
                    supplier={supplier} 
                    companyName={companyInfo.name}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== INTEGRATION ADVANTAGES ===== */}
      <section className="globalsupplier-py-5 globalsupplier-bg-white">
        <div className="globalsupplier-container py-4">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-primary px-4 py-2 rounded-pill mb-4">
                <i className="ri-flashlight-line me-2"></i>
                Integration Benefits
              </span>
              <h2 className="display-6 fw-bold mb-4">
                Why Suppliers Choose to Connect with {companyInfo.name}
              </h2>
              <p className="text-muted fs-5 mb-5">
                Join our growing network of leading travel suppliers and reach thousands of 
                verified travel agents through a single integration.
              </p>
              
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="ri-user-line fs-2" style={{ color: DefaultData.THEMECOLOR1 }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold">Verified Agents</h6>
                      <p className="text-muted small">Instant access to qualified B2B buyers</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="ri-api-line fs-2" style={{ color: DefaultData.THEMECOLOR1 }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold">One-Time Integration</h6>
                      <p className="text-muted small">Connect once, distribute everywhere</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="ri-bar-chart-line fs-2" style={{ color: DefaultData.THEMECOLOR1 }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold">Real-time Analytics</h6>
                      <p className="text-muted small">Track performance and demand patterns</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="ri-customer-service-line fs-2" style={{ color: DefaultData.THEMECOLOR1 }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold">24/7 Support</h6>
                      <p className="text-muted small">Dedicated technical support team</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bg-light p-5 rounded-5">
                <h4 className="fw-bold mb-4">Integration Process</h4>
                <div className="position-relative">
                  <div className="d-flex mb-4">
                    <div className="me-3">
                      <span className="badge bg-primary rounded-circle p-3">1</span>
                    </div>
                    <div>
                      <h6 className="fw-bold">API Documentation</h6>
                      <p className="text-muted mb-0">Comprehensive RESTful API documentation provided</p>
                    </div>
                  </div>
                  <div className="d-flex mb-4">
                    <div className="me-3">
                      <span className="badge bg-primary rounded-circle p-3">2</span>
                    </div>
                    <div>
                      <h6 className="fw-bold">Sandbox Testing</h6>
                      <p className="text-muted mb-0">Test integration in our staging environment</p>
                    </div>
                  </div>
                  <div className="d-flex mb-4">
                    <div className="me-3">
                      <span className="badge bg-primary rounded-circle p-3">3</span>
                    </div>
                    <div>
                      <h6 className="fw-bold">Go Live</h6>
                      <p className="text-muted mb-0">Deploy to production with 24/7 monitoring</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRICING SECTION ===== */}
      <PricingSection 
        buttonText="Start Free Trial" 
        onPlanClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} 
      />

      {/* ===== FAQ SECTION ===== */}
      <section className="globalsupplier-py-5 globalsupplier-bg-light">
        <div className="globalsupplier-container py-lg-5">
          <h2 className="display-6 fw-bold text-center mb-5">
            Supplier Integration FAQs
          </h2>

          <div className="row g-4">
            <div className="col-md-6">
              <h6 className="fw-bold">How long does integration take?</h6>
              <p className="text-muted small">
                Most suppliers complete integration within 1-2 weeks, depending on API complexity.
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold">What support do you provide?</h6>
              <p className="text-muted small">
                We provide dedicated technical support, documentation, and sandbox environment for testing.
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold">Is there a cost to integrate?</h6>
              <p className="text-muted small">
                No integration fees. Our partnership model is built on mutual growth.
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold">Which suppliers are currently connected?</h6>
              <p className="text-muted small">
                We're integrated with leading suppliers including Trip jack, TBO, Akbar travels, Viator, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoDemoModal 
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl={demoVideoUrl}
      />
    </div>
  );
}